import { useMemo, useState } from "react";
import { Button } from "../ui/Button";
import { Input, Select } from "../ui/Field";
import { Modal } from "../ui/Overlay";
import { Tag } from "../ui/Tag";
import { Thumb } from "../ui/Thumb";
import { useT } from "../../lib/i18n";
import { rarityStars } from "../../lib/card";
import { RANDOM_ITEM_REF, cardPickRow, isStaleCardRef, refLabel, type CardPickRow } from "../../lib/reward";

/**
 * 奖励项里选一张卡（物品卡 / idol 卡）：点开是选取弹窗，不是原生下拉（禅道 9081）。
 *
 * 物品卡上百张时原生 `<select>` 只能一路往下滚。弹窗里按编号、卡片名称、卡包、状态筛，
 * 点一行即选中并关掉。value 仍是原来那份下拉候选（基准语言名 `cardRef`），存进去的东西不变。
 *
 * 编号是**精确匹配**：填卡包编号（`1`）出这一包全部，填卡牌编号（`1-3`）出那一张——
 * 模糊匹配下搜 1 会把 10、11、21 都带出来（禅道 9002 同一个问题）。
 * 候选里有、名词表里查不到的引用（比如系统邮件用服务端给的那份）照样列出来，只是没有卡包和图。
 */
export function CardRefSelect({
  kind, value, options, onChange, disabled, className = "w-[200px]",
}: {
  kind: "itemCard" | "idolCard";
  value: string | null;
  /** 可选的引用；物品卡允许随机时由调用方把 RANDOM_ITEM_REF 放在第一个 */
  options: string[];
  onChange: (ref: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const stale = !!value && isStaleCardRef(kind, value);
  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        title={value ? refLabel(kind, value, t) : undefined}
        className={
          `${className} flex h-control flex-none items-center justify-between gap-1 rounded-base border px-2 text-left text-base outline-none ` +
          (disabled ? "cursor-not-allowed border-line bg-fill-light text-ink-disabled " : "bg-white hover:border-primary focus:border-primary ") +
          (stale ? "border-danger text-danger" : disabled ? "" : "border-line text-ink")
        }
      >
        <span className={`truncate ${value ? "" : "text-ink-placeholder"}`}>{value ? refLabel(kind, value, t) : t("dlg.select")}</span>
        <span aria-hidden className="text-xs text-ink-placeholder">▾</span>
      </button>
      {open && (
        <CardPickDialog kind={kind} value={value} options={options}
          onClose={() => setOpen(false)}
          onPick={(ref) => { onChange(ref); setOpen(false); }} />
      )}
    </>
  );
}

const fallbackRow = (ref: string): CardPickRow => ({
  ref, packId: "", packCode: "", packName: "", packBase: "", cardCode: "", cardName: ref, cardBase: ref, rarity: 0, image: "", packDisabled: false,
});

function CardPickDialog({ kind, value, options, onClose, onPick }: {
  kind: "itemCard" | "idolCard"; value: string | null; options: string[];
  onClose: () => void; onPick: (ref: string) => void;
}) {
  const t = useT();
  const [qCode, setQCode] = useState("");
  const [qName, setQName] = useState("");
  const [qPack, setQPack] = useState("");
  const [qState, setQState] = useState<"" | "on" | "off">("");
  const random = kind === "itemCard" && options.includes(RANDOM_ITEM_REF);
  const all = useMemo(() => options.filter((o) => o !== RANDOM_ITEM_REF).map((o) => cardPickRow(o) ?? fallbackRow(o)), [options]);
  const packs = useMemo(() => {
    const m = new Map<string, { code: string; name: string }>();
    for (const r of all) if (r.packId && !m.has(r.packId)) m.set(r.packId, { code: r.packCode, name: r.packName });
    return [...m.entries()];
  }, [all]);
  const code = qCode.trim().toLowerCase();
  const name = qName.trim().toLowerCase();
  const rows = all.filter((r) =>
    (!code || r.cardCode.toLowerCase() === code || r.packCode.toLowerCase() === code) &&
    (!name || r.cardName.toLowerCase().includes(name) || r.cardBase.toLowerCase().includes(name)) &&
    (!qPack || r.packId === qPack) &&
    (!qState || (qState === "on" ? !r.packDisabled : r.packDisabled)));
  const filtered = !!(qCode || qName || qPack || qState);
  const th = "sticky top-0 bg-fill-lighter px-2 py-1.5 text-left text-xs font-medium text-ink-secondary whitespace-nowrap";
  const td = "px-2 py-1.5 align-middle";
  const rowCls = (ref: string) => `cursor-pointer border-t border-line-lighter hover:bg-primary-50 ${ref === value ? "bg-primary-50" : ""}`;

  return (
    <Modal title={t("cardPick.title", { kind: t(kind === "itemCard" ? "card.itemCard" : "card.idolCard") })} width={820} onClose={onClose}
      footer={<Button onClick={onClose}>{t("common.cancel")}</Button>}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={qCode} onChange={setQCode} placeholder={t("cardPick.byCode")} className="w-[190px]" />
        <Input value={qName} onChange={setQName} placeholder={t("cardPick.byName")} className="w-[160px]" />
        <Select value={qPack} onChange={setQPack} className="w-[180px]">
          <option value="">{t("cardPick.allPacks")}</option>
          {packs.map(([id, p]) => <option key={id} value={id}>{p.code ? `${p.code} · ${p.name}` : p.name}</option>)}
        </Select>
        <Select value={qState} onChange={(v) => setQState(v as "" | "on" | "off")} className="w-[110px]">
          <option value="">{t("card.stateAll")}</option>
          <option value="on">{t("card.on")}</option>
          <option value="off">{t("card.off")}</option>
        </Select>
        {filtered && <Button size="sm" onClick={() => { setQCode(""); setQName(""); setQPack(""); setQState(""); }}>{t("flt.reset")}</Button>}
      </div>
      <div className="mb-2 text-xs text-ink-placeholder">{t("cardPick.hint", { n: all.length, m: rows.length })}</div>
      <div className="max-h-[52vh] overflow-auto rounded-base border border-line-lighter">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={th} style={{ width: 56 }} />
              <th className={th}>{t("card.col.code")}</th>
              <th className={th}>{t("cardPick.col.pack")}</th>
              <th className={th}>{t("cardPick.col.card")}</th>
              <th className={th}>{t("dlg.rarity")}</th>
              <th className={th}>{t("card.col.enabled")}</th>
            </tr>
          </thead>
          <tbody>
            {/* 随机物品不属于任何卡包：钉在第一行，不参与筛选 */}
            {random && (
              <tr className={rowCls(RANDOM_ITEM_REF)} onClick={() => onPick(RANDOM_ITEM_REF)}>
                <td className={td} />
                <td className={`${td} font-mono text-ink-secondary`}>*</td>
                <td className={td} colSpan={4}>
                  <span className="text-primary">{t("reward.randomItem")}</span>
                  {value === RANDOM_ITEM_REF && <Tag tone="info">{t("cardPick.current")}</Tag>}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.ref} className={rowCls(r.ref)} onClick={() => onPick(r.ref)}>
                <td className={td}>
                  {r.image ? <Thumb src={r.image} title={r.cardName} className="h-9 w-9 rounded-sm border border-line object-cover" /> : <span className="text-ink-placeholder">—</span>}
                </td>
                <td className={`${td} font-mono text-ink-secondary`}>{r.cardCode || "—"}</td>
                <td className={td}>{r.packName ? `${r.packCode ? `${r.packCode} · ` : ""}${r.packName}` : "—"}</td>
                <td className={td}>
                  <span className="mr-1.5">{r.cardName}</span>
                  {r.ref === value && <Tag tone="info">{t("cardPick.current")}</Tag>}
                </td>
                <td className={`${td} text-warning`}>{r.rarity ? rarityStars(r.rarity) : "—"}</td>
                <td className={td}>{r.packDisabled ? <Tag tone="info">{t("card.off")}</Tag> : <Tag tone="success">{t("card.on")}</Tag>}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td className={`${td} py-6 text-center text-ink-placeholder`} colSpan={6}>{t("cardPick.empty")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
